/* eslint-disable @typescript-eslint/no-unsafe-call */
import { AxiosError } from 'axios';
import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useHash } from 'react-use';
import { useGetLoginPrivilegeInfo } from '../../../../api/auth/auth';
import { GetLoginPrivilegeInfoParams } from '../../../../api/model';
import { useGetPsCheckApproveCount, useGetPsCheckCheckCount } from '../../../../api/ps-checks/ps-checks';
import { GetPsDrawingListDownloadQueryResult, useGetPsDrawingListDownload } from '../../../../api/pss/pss';
import { useDefaultOptions, useOptionsForBinary } from '../../../../hooks/api';
import { executeDownload } from '../../../../utils/download';
import BlobErrorModal from '../../../modules/BlobErrorModal';
import ConfirmModal from '../../../modules/ConfirmModal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';
import PsApprovalDialog from './Dialog/PsApprovalDialog';
import PsCheckAddDialog from './Dialog/PsCheckAddDialog';
import PsCheckDialog from './Dialog/PsCheckDialog';
import PsHmSubmitDialog from './Dialog/PsHmSubmitDialog';
import PsStateHistoryDialog from './Dialog/PsStateHistoryDialog';
import PsApprovalListTab, { PsApprovalListTabHandler } from './PsApprovalListTab';
import PsCheckListTab, { PsCheckListTabHandler } from './PsCheckListTab';
import { DialogType, Message, getDialogMessage, getIsAccess } from './PsListCommmon';
import PsTemporaryListTab, { PsTemporaryListTabHandler } from './PsTemporaryListTab';
import PsUpdateListTab, { PsUpdateListTabHandler } from './PsUpdateListTab';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

/**
 * ソート可能なテーブルヘッダー
 * @param param0
 * @returns
 */
export function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

interface TabLiProps {
  index: number;
  selectedTabIndex: string;
  label: DefaultTFuncReturn;
  isDisabled: boolean;
}

/**
 * PS一覧画面の上部のタブ部分
 * @param param0
 * @returns
 */
function TabLi({ index, selectedTabIndex, label, isDisabled }: TabLiProps) {
  return (
    <li>
      <label
        id={`ps-tab-list${index}`}
        className={`ps-tab-list ${selectedTabIndex === `#${index}` ? 'current' : ''}`}
        htmlFor={`ps-tab-btn${index}`}
        tabIndex={isDisabled ? undefined : 0}
        onKeyDown={(e) => {
          if (e.code === 'Space') {
            document.getElementById(`ps-tab-list${index}`)?.click();
            e.preventDefault();
          }
        }}
      >
        {label}
      </label>
    </li>
  );
}

/**
 * PS一覧画面 タブ表示
 */
function PsListForm() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { hash } = useLocation();
  const [, setHashTabIndex] = useHash();

  // ------------ useState
  // 現在選択中タブインデックス
  const [selectedTabIndex, setSelectedTabIndex] = useState(hash);
  // ダイアログ用メッセージ
  const [message, setMessage] = useState(getDialogMessage(Message.DEFAULT, t));
  // 表示モーダル判定
  const [activeModal, setActiveModal] = useState(DialogType.NONE);
  // 対象バージョンID
  const targetVersionId = useRef<number>(-1);
  // 対象インデックス(PS更新一覧、PS承認一覧で使用)
  const targetIndex = useRef<number>(-1);
  // 改廃案履歴ID
  const psTemporaryStateHistoryId = useRef<number>(-1);
  // 閲覧モードかどうか
  const isViewMode = useRef<boolean>(false);
  // タブを操作するための ref
  const refPsUpdateListTab = useRef<PsUpdateListTabHandler>(null);
  const refPsTemporaryListTab = useRef<PsTemporaryListTabHandler>(null);
  const refPsCheckListTab = useRef<PsCheckListTabHandler>(null);
  const refPsApprovalListTab = useRef<PsApprovalListTabHandler>(null);
  const downloadErrorShow = useRef<boolean>(false);

  const options = useDefaultOptions(language);
  const paramForCheck = { function_id: 44 } as GetLoginPrivilegeInfoParams;
  const paramForApproval = { function_id: 45 } as GetLoginPrivilegeInfoParams;
  // ログインユーザ情報取得
  const psLoginPrivilegeInfoCheck = useGetLoginPrivilegeInfo(paramForCheck, options);
  const psLoginPrivilegeInfoApproval = useGetLoginPrivilegeInfo(paramForApproval, options);
  // 確認申請タブを表示するかどうか
  const isCheckUser = getIsAccess(psLoginPrivilegeInfoCheck.data?.data.user_privileges ?? []);
  // 承認申請タブを表示するかどうか
  const isApprovalUser = getIsAccess(psLoginPrivilegeInfoApproval.data?.data.user_privileges ?? []);

  // 確認申請タブに表示する件数
  const cntCheck = useGetPsCheckCheckCount(options);
  // 承認申請タブに表示する件数
  const cntApprove = useGetPsCheckApproveCount(options);

  // 図面リスト出力 API 準備
  const optionsForBinary = useOptionsForBinary(language);
  const { refetch, isError, error } = useGetPsDrawingListDownload<
    GetPsDrawingListDownloadQueryResult,
    AxiosError<Blob>
  >(optionsForBinary);
  // 図面リスト出力 エラーハンドリング用
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  // 図面リスト出力中判定用
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  // 図面リスト出力
  const download = useCallback(async () => {
    // エラーがあるなしに限りなくダウンロード実行時に初回メッセージフラグをtrueにする
    downloadErrorShow.current = true;
    setDisplaySpinnerModal(true);
    const response = await refetch();
    setDisplaySpinnerModal(false);
    if (response.data != null) {
      executeDownload(response.data);
    } else if (response.isError) {
      setDisplayDownloadError(true);
    }
  }, [refetch]);

  // モーダル呼出関数
  const callModal = useCallback(
    (
      psVersionId: number | undefined,
      tempState: number,
      selectedIndex: number,
      isView = false,
      stateHistoryId = -1
    ) => {
      targetVersionId.current = psVersionId ?? 0;
      targetIndex.current = selectedIndex;
      setActiveModal(tempState);
      isViewMode.current = isView;
      if (isView) {
        // 閲覧モードなら、表示する状態番号をコンポーネントに通知する。
        psTemporaryStateHistoryId.current = stateHistoryId;
      }
    },
    []
  );

  // 次PS確認関数(確認申請タブ)
  const checkCheckNextPs = useCallback(async () => {
    // 確認申請タブの次のPSを確認する関数を呼び出す
    const { psVersionId: nextPsVersionId, index: nextIndex } = (await refPsCheckListTab.current?.checkNextPs(
      targetIndex.current
    )) ?? {
      psVersionId: -1,
      index: -1,
    };
    if (nextPsVersionId !== -1) {
      targetVersionId.current = nextPsVersionId;
      targetIndex.current = nextIndex;
      setActiveModal(DialogType.CONFIRM_CHECK_NEXT_PS);
    } else {
      // 次のPSはないというメッセージ表示
      setMessage(getDialogMessage(Message.NO_NEXT_CHECK_PS, t));
      setActiveModal(DialogType.MESSAGE);
    }
  }, [t]);

  // 次PS確認関数(承認申請タブ)
  const checkApproveNextPs = useCallback(async () => {
    // 承認申請タブの次のPSを確認する関数を呼び出す
    const { psVersionId: nextPsVersionId, index: nextIndex } = (await refPsApprovalListTab.current?.checkNextPs(
      targetIndex.current
    )) ?? {
      psVersionId: -1,
      index: -1,
    };
    if (nextPsVersionId !== -1) {
      targetVersionId.current = nextPsVersionId;
      targetIndex.current = nextIndex;
      setActiveModal(DialogType.CONFIRM_APPROVE_NEXT_PS);
    } else {
      // 次のPSはないというメッセージ表示
      setMessage(getDialogMessage(Message.NO_NEXT_APPROVE_PS, t));
      setActiveModal(DialogType.MESSAGE);
    }
  }, [t]);

  // 全タブ リロード関数
  const setReloadAll = useCallback(() => {
    void cntCheck.refetch();
    void cntApprove.refetch();
    void refPsUpdateListTab.current?.refresh();
    void refPsTemporaryListTab.current?.refresh();
    void refPsCheckListTab.current?.refresh();
    void refPsApprovalListTab.current?.refresh();
  }, [cntApprove, cntCheck]);

  // PS更新履歴一覧リロード関数
  const setUpdatelistReload = useCallback(() => {
    void refPsUpdateListTab.current?.refresh();
  }, []);

  // PS改廃案一覧リロード関数
  const setTempReload = useCallback(() => {
    void refPsTemporaryListTab.current?.refresh();
  }, []);

  // PS確認タブリロード関数
  const setCheckReload = useCallback(() => {
    void refPsCheckListTab.current?.refresh();
  }, []);

  // PS承認タブリロード関数
  const setApprovalReload = useCallback(() => {
    void refPsApprovalListTab.current?.refresh();
  }, []);

  useEffect(() => {
    // 権限がないのに、確認申請タブを表示しようとした場合、無視
    if (!isCheckUser && hash === '#3') {
      return;
    }
    // 権限がないのに、承認申請タブを表示しようとした場合、無視
    if (!isApprovalUser && hash === '#4') {
      return;
    }
    setSelectedTabIndex(`${hash}`);
    setDisplayDownloadError(isError && downloadErrorShow.current);
  }, [hash, isApprovalUser, isCheckUser, isError]);

  return (
    <section>
      <div className="ps-tab-area">
        <input
          id="ps-tab-btn1"
          className="ps-tab-btn"
          name="ps-tab"
          type="radio"
          checked={selectedTabIndex === '#1'}
          onChange={() => {
            setHashTabIndex('#1');
            setSelectedTabIndex('#1');
            setUpdatelistReload();
          }}
        />
        <input
          id="ps-tab-btn2"
          className="ps-tab-btn"
          name="ps-tab"
          type="radio"
          checked={selectedTabIndex === '#2'}
          onChange={() => {
            setHashTabIndex('#2');
            setSelectedTabIndex('#2');
            setTempReload();
          }}
        />
        <input
          id="ps-tab-btn3"
          className="ps-tab-btn"
          name="ps-tab"
          type="radio"
          checked={selectedTabIndex === '#3'}
          onChange={() => {
            setHashTabIndex('#3');
            setSelectedTabIndex('#3');
            setCheckReload();
          }}
        />
        <input
          id="ps-tab-btn4"
          className="ps-tab-btn"
          name="ps-tab"
          type="radio"
          checked={selectedTabIndex === '#4'}
          onChange={() => {
            setHashTabIndex('#4');
            setSelectedTabIndex('#4');
            setApprovalReload();
          }}
        />

        <div className="pagetabmenufr2">
          <ul className="ps-tab-list-wrap">
            <TabLi index={1} isDisabled={false} label={t('PS更新情報')} selectedTabIndex={selectedTabIndex} />
            <TabLi index={2} isDisabled={false} label={t('PS改廃案一覧')} selectedTabIndex={selectedTabIndex} />
            {isCheckUser && (
              <TabLi
                index={3}
                isDisabled={!isCheckUser}
                label={`${t('確認申請')}(${cntCheck.data?.data?.toString() ?? ''})`}
                selectedTabIndex={selectedTabIndex}
              />
            )}
            {isApprovalUser && (
              <TabLi
                index={4}
                isDisabled={!isApprovalUser}
                label={`${t('承認申請')}(${cntApprove.data?.data?.toString() ?? ''})`}
                selectedTabIndex={selectedTabIndex}
              />
            )}
          </ul>
          <div className="listdlfr">
            <p className="takein cursor">
              <button type="button" className="text-button" onClick={download}>
                <i className="fa-solid fa-download" />
                {t('図面リスト出力')}
              </button>
            </p>
          </div>
        </div>

        <div className="ps-tab-content-wrap wfr">
          {/* PS更新履歴一覧タブ */}
          <div id="ps-tab-content1" className="ps-tab-content in-pgnavi">
            <PsUpdateListTab ref={refPsUpdateListTab} />
          </div>
          {/* PS改廃案一覧画面タブ */}
          <div id="ps-tab-content2" className="ps-tab-content in-pgnavi">
            <PsTemporaryListTab ref={refPsTemporaryListTab} callModal={callModal} />
          </div>
          {/* 確認権限がある場合のみ表示 */}
          {isCheckUser && (
            <div id="ps-tab-content3" className="ps-tab-content in-pgnavi">
              <PsCheckListTab ref={refPsCheckListTab} callModal={callModal} />
            </div>
          )}
          {/* 承認権限がある場合のみ表示 */}
          {isApprovalUser && (
            <div id="ps-tab-content4" className="ps-tab-content in-pgnavi">
              <PsApprovalListTab ref={refPsApprovalListTab} callModal={callModal} />
            </div>
          )}
          {/* 状態履歴ダイアログ */}
          {activeModal === DialogType.STATE_HISTORY && (
            <PsStateHistoryDialog
              onClose={() => {
                setActiveModal(DialogType.NONE);
              }}
              versionId={targetVersionId.current}
              setRefresh={setReloadAll}
            />
          )}
          {/* HM提出ダイアログ */}
          {activeModal === DialogType.HM_SUBMISSION && (
            <PsHmSubmitDialog
              onClose={() => {
                setActiveModal(DialogType.NONE);
              }}
              displayCloseButton={false}
              versionId={targetVersionId.current}
              state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
              setActiveModal={setActiveModal}
              isViewMode={isViewMode.current}
              setRefresh={setReloadAll}
              setMessage={setMessage}
            />
          )}
          {/* 確認申請ダイアログ */}
          {activeModal === DialogType.APPLICATION_FOR_CONFIRMATION && (
            <PsCheckAddDialog
              onClose={() => {
                setActiveModal(DialogType.NONE);
              }}
              displayCloseButton={false}
              versionId={targetVersionId.current}
              state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
              setActiveModal={setActiveModal}
              isViewMode={isViewMode.current}
              setRefresh={setReloadAll}
              setMessage={setMessage}
            />
          )}
          {/* 内容確認（GL） */}
          {activeModal === DialogType.CONTENT_CONFIRMATION_GL && (
            <PsCheckDialog
              onClose={() => {
                setActiveModal(DialogType.NONE);
              }}
              displayCloseButton={false}
              versionId={targetVersionId.current}
              state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
              setActiveModal={setActiveModal}
              isViewMode={isViewMode.current}
              setRefresh={setReloadAll}
              setMessage={setMessage}
              targetIndex={targetIndex.current}
              checkNextPs={checkCheckNextPs}
            />
          )}
          {/* 内容確認（管理者） */}
          {activeModal === DialogType.CONTENT_CONFIRMATION_MANAGER && (
            <PsApprovalDialog
              onClose={() => {
                setActiveModal(DialogType.NONE);
              }}
              displayCloseButton={false}
              versionId={targetVersionId.current}
              state={{ ps_temporary_state_history_id: psTemporaryStateHistoryId.current }}
              setActiveModal={setActiveModal}
              isViewMode={isViewMode.current}
              setRefresh={setReloadAll}
              setMessage={setMessage}
              targetIndex={targetIndex.current}
              checkNextPs={checkApproveNextPs}
            />
          )}
          {/* 確認メッセージダイアログ */}
          {activeModal === DialogType.MESSAGE && (
            <OkModal
              onClickOk={() => {
                setActiveModal(DialogType.NONE);
              }}
            >
              {message}
            </OkModal>
          )}
          {/* 次PS確認メッセージ(確認申請) */}
          {activeModal === DialogType.CONFIRM_CHECK_NEXT_PS && (
            <ConfirmModal
              onClickOk={async () => {
                // 次のPS情報を取得してモーダル表示
                const { index: idx } = (await refPsCheckListTab.current?.checkNextPs(targetIndex.current)) ?? {
                  index: -1,
                };
                if (idx !== -1) {
                  callModal(targetVersionId.current, DialogType.CONTENT_CONFIRMATION_GL, idx);
                } else {
                  setMessage(getDialogMessage(Message.NO_NEXT_CHECK_PS, t));
                  setActiveModal(DialogType.MESSAGE);
                }
              }}
              onClickCancel={() => {
                setActiveModal(DialogType.NONE);
              }}
            >
              <div className="poptxt">
                <p>{getDialogMessage(Message.CONFIRM_NEXT_PS, t)}</p>
              </div>
            </ConfirmModal>
          )}
          {/* 次PS確認メッセージ(承認申請) */}
          {activeModal === DialogType.CONFIRM_APPROVE_NEXT_PS && (
            <ConfirmModal
              onClickOk={async () => {
                // 次のPS情報を取得してモーダル表示
                const { index: idx } = (await refPsApprovalListTab.current?.checkNextPs(targetIndex.current)) ?? {
                  index: -1,
                };
                if (idx !== -1) {
                  callModal(targetVersionId.current, DialogType.CONTENT_CONFIRMATION_MANAGER, idx);
                } else {
                  setMessage(getDialogMessage(Message.NO_NEXT_APPROVE_PS, t));
                  setActiveModal(DialogType.MESSAGE);
                }
              }}
              onClickCancel={() => {
                setActiveModal(DialogType.NONE);
              }}
            >
              <div className="poptxt">
                <p>{getDialogMessage(Message.CONFIRM_NEXT_PS, t)}</p>
              </div>
            </ConfirmModal>
          )}
          {/* スピナーモーダル */}
          {displaySpinnerModal && <SpinnerModal />}
          {/* ダウンロードエラー */}
          {displayDownloadError && (
            <BlobErrorModal
              error={error}
              onClickOk={() => {
                setDisplayDownloadError(false);
                downloadErrorShow.current = false;
              }}
            />
          )}
        </div>
      </div>
    </section>
  );
}

export default PsListForm;
